import React, { useEffect, useState } from 'react';
import { healthcheckAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { HeartPulse, Activity, AlertTriangle, XCircle, Hand, Hash, Clock, Search } from 'lucide-react';

const INTERVAL_PRESETS = [
  { label: '10 мин', value: '10m' },
  { label: '1 час', value: '1h' },
  { label: '24 часа', value: '24h' },
  { label: '48 часов', value: '48h' },
  { label: '1 неделя', value: '7d' },
];

const Healthcheck = () => {
  const { showSuccess, showError } = useNotification();
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configForm, setConfigForm] = useState({
    enabled: false,
    schedule_type: 'interval',
    schedule_expr: '7d',
    batch_size: 100,
    request_delay_ms: 500,
    request_timeout_s: 10,
    rate_limit: 0,
    stale_threshold: 3,
    gone_threshold_days: 30,
    check_locations: true,
    locations_sample_size: 10,
  });

  // History modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [statusChanges, setStatusChanges] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Bulk delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetStatus, setDeleteTargetStatus] = useState('');
  const [deleteHosts, setDeleteHosts] = useState([]);
  const [selectedHostIds, setSelectedHostIds] = useState(new Set());
  const [deleteFilter, setDeleteFilter] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Per-run changes modal
  const [showRunChanges, setShowRunChanges] = useState(false);
  const [runChanges, setRunChanges] = useState([]);
  const [runChangesLoading, setRunChangesLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, runsRes, statusRes] = await Promise.all([
        healthcheckAPI.getConfig(),
        healthcheckAPI.listRuns(),
        healthcheckAPI.getStatus(),
      ]);
      const cfg = cfgRes.data;
      setConfig(cfg);
      setConfigForm({
        enabled: cfg.enabled || false,
        schedule_type: cfg.schedule?.type || 'interval',
        schedule_expr: cfg.schedule?.expr || '7d',
        batch_size: cfg.batch_size || 100,
        request_delay_ms: cfg.request_delay_ms || 500,
        request_timeout_s: cfg.request_timeout_s || 10,
        rate_limit: cfg.rate_limit || 0,
        stale_threshold: cfg.stale_threshold || 3,
        gone_threshold_days: cfg.gone_threshold_days || 30,
        check_locations: cfg.check_locations !== false,
        locations_sample_size: cfg.locations_sample_size || 10,
      });
      setRuns(runsRes.data.runs || []);
      setStatus(statusRes.data);
    } catch (e) {
      showError('Не удалось загрузить данные проверки доступности');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 15000);
    return () => clearInterval(interval);
  }, []);

  const saveConfig = async () => {
    try {
      await healthcheckAPI.updateConfig({
        enabled: configForm.enabled,
        schedule: { type: configForm.schedule_type, expr: configForm.schedule_expr },
        batch_size: parseInt(configForm.batch_size) || 100,
        request_delay_ms: parseInt(configForm.request_delay_ms) || 500,
        request_timeout_s: parseInt(configForm.request_timeout_s) || 10,
        rate_limit: parseInt(configForm.rate_limit) || 0,
        stale_threshold: parseInt(configForm.stale_threshold) || 3,
        gone_threshold_days: parseInt(configForm.gone_threshold_days) || 30,
        check_locations: configForm.check_locations,
        locations_sample_size: parseInt(configForm.locations_sample_size) || 10,
      });
      showSuccess('Настройки сохранены');
      loadAll();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось сохранить настройки');
    }
  };

  const runNow = async () => {
    try {
      await healthcheckAPI.runNow();
      showSuccess('Проверка запущена');
      loadAll();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось запустить проверку');
    }
  };

  const cancelRun = async (runId) => {
    try {
      await healthcheckAPI.cancelRun(runId);
      showSuccess('Проверка отменена');
      loadAll();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось отменить');
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await healthcheckAPI.getStatusChanges(1, 100);
      setStatusChanges(res.data.changes || []);
    } catch (e) {
      showError('Не удалось загрузить историю');
    } finally {
      setHistoryLoading(false);
    }
  };

  const viewRunChanges = async (runId) => {
    setRunChangesLoading(true);
    setShowRunChanges(true);
    try {
      const res = await healthcheckAPI.getRunChanges(runId);
      setRunChanges(res.data.changes || []);
    } catch (e) {
      showError('Не удалось загрузить изменения');
      setRunChanges([]);
    } finally {
      setRunChangesLoading(false);
    }
  };

  const openDeleteModal = async (targetStatus) => {
    setDeleteTargetStatus(targetStatus);
    setDeleteLoading(true);
    setShowDeleteModal(true);
    setSelectedHostIds(new Set());
    setDeleteFilter('');
    try {
      const res = await healthcheckAPI.getHostsByStatus(targetStatus);
      setDeleteHosts(res.data.hosts || []);
    } catch (e) {
      showError('Не удалось загрузить список хостов');
      setDeleteHosts([]);
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteSelectedHosts = async () => {
    if (selectedHostIds.size === 0) return;
    if (!window.confirm(`Удалить ${selectedHostIds.size} хостов? Также будут удалены комментарии, уязвимости и классификации. Действие необратимо.`)) return;
    try {
      const res = await healthcheckAPI.deleteSelectedHosts([...selectedHostIds]);
      showSuccess(`Удалено ${res.data.deleted_hosts} хостов`);
      setShowDeleteModal(false);
      loadAll();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить');
    }
  };

  const filteredDeleteHosts = deleteHosts.filter(h =>
    !deleteFilter || h.host?.toLowerCase().includes(deleteFilter.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedHostIds.size === filteredDeleteHosts.length) {
      setSelectedHostIds(new Set());
    } else {
      setSelectedHostIds(new Set(filteredDeleteHosts.map(h => h._id)));
    }
  };

  const toggleHostSelect = (id) => {
    const next = new Set(selectedHostIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedHostIds(next);
  };

  const formatBytes = (n) => {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const checkBadgeStyle = (entry) => {
    // Returns { bg, color, border, label, tip }
    const e = entry || {};
    const sc = e.status_code;
    const err = e.error;

    const scStyle = (code) => {
      if (code >= 200 && code < 300) return { bg: 'rgba(34,197,94,0.12)', color: '#16a34a', border: 'rgba(34,197,94,0.35)' };
      if (code >= 300 && code < 400) return { bg: 'rgba(59,130,246,0.12)', color: '#2563eb', border: 'rgba(59,130,246,0.35)' };
      if (code >= 400 && code < 500) return { bg: 'rgba(234,179,8,0.14)', color: '#a16207', border: 'rgba(234,179,8,0.4)' };
      return { bg: 'rgba(239,68,68,0.12)', color: '#dc2626', border: 'rgba(239,68,68,0.35)' };
    };

    // Случай 1: получен HTTP-статус
    if (sc != null) {
      return { ...scStyle(sc), label: String(sc), tip: err ? `HTTP ${sc} (${err})` : `HTTP ${sc}` };
    }

    // Случай 2: gateway_error_5xx — показываем числовой код
    if (err && typeof err === 'string' && err.startsWith('gateway_error_')) {
      const code = parseInt(err.replace('gateway_error_', ''), 10);
      if (!isNaN(code)) {
        return { ...scStyle(code), label: String(code), tip: `Gateway error: HTTP ${code}` };
      }
    }

    // Случай 3: известные категории ошибок
    if (err === 'timeout') {
      return {
        bg: 'rgba(234,88,12,0.14)', color: '#c2410c', border: 'rgba(234,88,12,0.35)',
        label: 'Timeout', tip: 'Таймаут запроса (по умолчанию 10с)',
      };
    }
    if (err === 'connection_error') {
      return {
        bg: 'rgba(185,28,28,0.14)', color: '#b91c1c', border: 'rgba(185,28,28,0.35)',
        label: 'Conn', tip: 'Connection refused: хост недоступен или закрыл соединение',
      };
    }

    // Случай 4: всё остальное (DNS, TLS, прочие ошибки Python)
    if (err) {
      return {
        bg: 'rgba(107,33,168,0.14)', color: '#6b21a8', border: 'rgba(107,33,168,0.35)',
        label: 'Err', tip: String(err),
      };
    }

    return null;
  };

  const renderRecentChecks = (host) => {
    const checks = Array.isArray(host.recent_checks) ? host.recent_checks.slice(0, 3) : [];
    if (checks.length === 0) return <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>—</span>;
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {checks.map((c, i) => {
          const s = checkBadgeStyle(c);
          if (!s) return null;
          return (
            <span
              key={i}
              title={`${s.tip}${c.ts ? ` • ${new Date(c.ts).toLocaleString()}` : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '36px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                borderRadius: '6px',
                backgroundColor: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
              }}
            >
              {s.label}
            </span>
          );
        })}
      </div>
    );
  };

  const getChangeBadge = (changes) => {
    if (changes?.status_recovered) return { cls: 'badge-info', label: 'Восстановлен' };
    if (changes?.removed_host) return { cls: 'badge-danger', label: 'Недоступен' };
    if (changes?.host_changed) return { cls: 'badge-warning', label: 'Устарел' };
    if (changes?.links_changed) return { cls: 'badge-neutral', label: 'Локации' };
    return { cls: 'badge-neutral', label: 'Изменение' };
  };

  const statusCards = [
    { key: 'active', label: 'Активные', icon: Activity, badge: 'badge-success' },
    { key: 'stale', label: 'Устаревшие', icon: AlertTriangle, badge: 'badge-warning' },
    { key: 'gone', label: 'Недоступные', icon: XCircle, badge: 'badge-danger' },
    { key: 'manual', label: 'Ручные', icon: Hand, badge: 'badge-info' },
    { key: 'total', label: 'Всего', icon: Hash, badge: null },
  ];

  return (
    <div style={{ padding: '28px' }}>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-icon">
          <HeartPulse size={24} />
        </div>
        <div className="page-header-text">
          <h2>Проверка доступности</h2>
          <p>Мониторинг состояния хостов</p>
        </div>
      </div>

      {/* Status cards */}
      {status && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '28px' }}>
          {statusCards.map(({ key, label, icon: Icon, badge }) => (
            <div key={key} className="stat-card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: badge ? undefined : 'var(--color-neutral-bg)',
                }} className={badge || ''}>
                  <Icon size={20} />
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {status[key] || 0}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => { setShowHistoryModal(true); loadHistory(); }}>
          <Clock size={14} />
          История изменений
        </button>
        {status?.stale > 0 && (
          <button
            className="btn"
            style={{ backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', border: '1px solid var(--color-warning-border)' }}
            onClick={() => openDeleteModal('stale')}
          >
            <AlertTriangle size={14} />
            Удалить устаревшие ({status.stale})
          </button>
        )}
        {status?.gone > 0 && (
          <button
            className="btn"
            style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-text)', border: '1px solid var(--color-danger-border)' }}
            onClick={() => openDeleteModal('gone')}
          >
            <XCircle size={14} />
            Удалить недоступные ({status.gone})
          </button>
        )}
      </div>

      {/* Config + Runs */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '24px', alignItems: 'start' }}>
        {/* Configuration */}
        <div className="card" style={{ padding: '24px' }}>
          <div className="section-header">Настройки</div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '16px', fontSize: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={configForm.enabled} onChange={e => setConfigForm({ ...configForm, enabled: e.target.checked })} />
            Включено
          </label>

          {/* Schedule */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Расписание</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <select value={configForm.schedule_type} onChange={e => setConfigForm({ ...configForm, schedule_type: e.target.value })} className="form-input" style={{ width: 'auto', padding: '8px 12px' }}>
                <option value="interval">Интервал</option>
                <option value="cron">Cron</option>
              </select>
              <input
                className="form-input"
                value={configForm.schedule_expr}
                onChange={e => setConfigForm({ ...configForm, schedule_expr: e.target.value })}
                placeholder={configForm.schedule_type === 'cron' ? '0 3 * * 0' : '7d'}
                style={{ flex: 1 }}
              />
            </div>
            {/* Presets */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {INTERVAL_PRESETS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setConfigForm({ ...configForm, schedule_type: 'interval', schedule_expr: p.value })}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: configForm.schedule_type === 'interval' && configForm.schedule_expr === p.value ? 'var(--color-primary)' : 'var(--bg-secondary)',
                    color: configForm.schedule_type === 'interval' && configForm.schedule_expr === p.value ? 'white' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <label>Размер пакета</label>
            <input type="number" value={configForm.batch_size} onChange={e => setConfigForm({ ...configForm, batch_size: e.target.value })} style={{ width: '80px' }} />
          </div>

          <div className="form-row">
            <label>Задержка</label>
            <input type="number" value={configForm.request_delay_ms} onChange={e => setConfigForm({ ...configForm, request_delay_ms: e.target.value })} style={{ width: '80px' }} />
            <span className="hint">мс</span>
          </div>

          <div className="form-row">
            <label>Rate limit</label>
            <input type="number" value={configForm.rate_limit} onChange={e => setConfigForm({ ...configForm, rate_limit: e.target.value })} style={{ width: '80px' }} />
            <span className="hint">req/s (0 = выкл)</span>
          </div>

          <div className="form-row">
            <label>Таймаут</label>
            <input type="number" value={configForm.request_timeout_s} onChange={e => setConfigForm({ ...configForm, request_timeout_s: e.target.value })} style={{ width: '80px' }} />
            <span className="hint">сек</span>
          </div>

          <div className="form-row">
            <label>Устаревание</label>
            <input type="number" value={configForm.stale_threshold} onChange={e => setConfigForm({ ...configForm, stale_threshold: e.target.value })} style={{ width: '64px' }} />
            <span className="hint">неудач</span>
          </div>

          <div className="form-row">
            <label>Недоступность</label>
            <input type="number" value={configForm.gone_threshold_days} onChange={e => setConfigForm({ ...configForm, gone_threshold_days: e.target.value })} style={{ width: '64px' }} />
            <span className="hint">дней</span>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '12px', fontSize: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={configForm.check_locations} onChange={e => setConfigForm({ ...configForm, check_locations: e.target.checked })} />
            Проверять локации
          </label>

          {configForm.check_locations && (
            <div className="form-row">
              <label>Выборка</label>
              <input type="number" value={configForm.locations_sample_size} onChange={e => setConfigForm({ ...configForm, locations_sample_size: e.target.value })} style={{ width: '64px' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            <button className="btn btn-primary" onClick={saveConfig}>Сохранить</button>
            <button className="btn btn-secondary" onClick={runNow} disabled={status?.is_running}>
              {status?.is_running ? 'Выполняется...' : 'Запустить'}
            </button>
          </div>
        </div>

        {/* Runs History */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-color)' }}>
            <div className="section-header" style={{ marginBottom: 0 }}>История запусков</div>
          </div>
          <table className="table" style={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
            <thead>
              <tr>
                <th>Создан</th>
                <th>Статус</th>
                <th>Проверено</th>
                <th>Доступно</th>
                <th>Недоступно</th>
                <th>Изменения</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-tertiary)' }}>Запусков пока нет</td></tr>
              )}
              {runs.map(r => (
                <tr key={r._id}>
                  <td style={{ fontSize: '13px' }}>{new Date(r.created_at).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${
                      r.status === 'completed' ? 'badge-success' :
                      r.status === 'running' ? 'badge-info' :
                      r.status === 'failed' ? 'badge-danger' :
                      'badge-neutral'
                    }`}>
                      {r.status === 'completed' ? 'завершён' :
                       r.status === 'running' ? 'выполняется' :
                       r.status === 'failed' ? 'ошибка' :
                       r.status === 'pending' ? 'в ожидании' :
                       r.status === 'cancelled' ? 'отменён' : r.status}
                    </span>
                  </td>
                  <td>{r.stats?.checked ?? '-'}</td>
                  <td>{r.stats?.reachable ?? '-'}</td>
                  <td>{r.stats?.unreachable ?? '-'}</td>
                  <td>{r.stats?.diffs_created ?? '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {(r.status === 'running' || r.status === 'pending') && (
                        <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => cancelRun(r._id)}>Отменить</button>
                      )}
                      {r.status === 'completed' && r.stats?.diffs_created > 0 && (
                        <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => viewRunChanges(r._id)}>Детали</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '800px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>История изменений статусов</h3>
              <button className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Закрыть</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>Загрузка...</div>
              ) : statusChanges.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-tertiary)' }}>Изменений пока нет</div>
              ) : (
                <table className="table" style={{ boxShadow: 'none' }}>
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Хост</th>
                      <th>Тип</th>
                      <th>Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusChanges.map(c => {
                      const badge = getChangeBadge(c.changes);
                      return (
                        <tr key={c._id}>
                          <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{new Date(c.detected_at).toLocaleString()}</td>
                          <td style={{ fontSize: '13px', fontWeight: '500' }}>{c.host}</td>
                          <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                          <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{(c.changes?.changes_summary || []).join('; ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '1100px', maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {deleteTargetStatus === 'stale' ? 'Устаревшие' : 'Недоступные'} хосты ({deleteHosts.length})
              </h3>
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Закрыть</button>
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                className="form-input"
                placeholder="Поиск по хосту..."
                value={deleteFilter}
                onChange={e => setDeleteFilter(e.target.value)}
                style={{ paddingLeft: '36px' }}
              />
            </div>

            <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
              {deleteLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>Загрузка...</div>
              ) : (
                <table className="table" style={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input type="checkbox" checked={selectedHostIds.size === filteredDeleteHosts.length && filteredDeleteHosts.length > 0} onChange={toggleSelectAll} />
                      </th>
                      <th>Хост</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Последние 3 запуска</th>
                      <th>Заголовок</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Размер</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Последняя проверка</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Неудач</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeleteHosts.map(h => (
                      <tr key={h._id} style={{ cursor: 'pointer' }} onClick={() => toggleHostSelect(h._id)}>
                        <td onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedHostIds.has(h._id)} onChange={() => toggleHostSelect(h._id)} />
                        </td>
                        <td style={{ fontSize: '13px', fontWeight: '500', wordBreak: 'break-all' }}>{h.host}</td>
                        <td>{renderRecentChecks(h)}</td>
                        <td
                          title={h.last_title || ''}
                          style={{
                            fontSize: '12px',
                            color: h.last_title ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            maxWidth: '260px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h.last_title || '—'}
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                          {formatBytes(h.last_size)}
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h.last_healthcheck ? new Date(h.last_healthcheck).toLocaleString() : '—'}</td>
                        <td style={{ fontSize: '13px', textAlign: 'center' }}>{h.healthcheck_failures ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Выбрано: {selectedHostIds.size} из {filteredDeleteHosts.length}
              </span>
              <button className="btn btn-danger" onClick={deleteSelectedHosts} disabled={selectedHostIds.size === 0}>
                Удалить выбранные ({selectedHostIds.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-Run Changes Modal */}
      {showRunChanges && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '800px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Изменения в этом запуске</h3>
              <button className="btn btn-secondary" onClick={() => setShowRunChanges(false)}>Закрыть</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {runChangesLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>Загрузка...</div>
              ) : runChanges.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-tertiary)' }}>Нет изменений</div>
              ) : (
                <table className="table" style={{ boxShadow: 'none' }}>
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Хост</th>
                      <th>Тип</th>
                      <th>Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runChanges.map(c => {
                      const badge = getChangeBadge(c.changes);
                      return (
                        <tr key={c._id}>
                          <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{new Date(c.detected_at).toLocaleString()}</td>
                          <td style={{ fontSize: '13px', fontWeight: '500' }}>{c.host}</td>
                          <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                          <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{(c.changes?.changes_summary || []).join('; ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Healthcheck;
